import type { ScanAnalyticsScope } from "types/scanAnalytics";

function scopeKey(scope?: ScanAnalyticsScope): string {
  if (!scope) return "";
  return [
    scope.employeeScopeId ?? "",
    scope.companyScopeId ?? "",
    scope.individualScopeId ?? "",
  ].join(":");
}

export const queryKeys = {
  company: {
    accountType: (companyId: string | number) =>
      ["company", "accountType", companyId] as const,
  },
  employees: {
    all: ["employees"] as const,
    detail: (id: string) => ["employees", "detail", id] as const,
    titles: (includeId?: string | number | null) =>
      ["employees", "titles", includeId ?? ""] as const,
    list: (
      page: number,
      pageSize: number,
      options: {
        companyId?: number;
        search?: string;
        companySearch?: string;
        isActive?: boolean;
      }
    ) => ["employees", "list", page, pageSize, options] as const,
  },
  companies: {
    all: ["companies"] as const,
    detail: (id: string) => ["companies", "detail", id] as const,
    info: (id: string) => ["companies", "info", id] as const,
    list: (
      page: number,
      pageSize: number,
      options: {
        search?: string;
        packageId?: string | number;
        fromDate?: string | null;
        toDate?: string | null;
      }
    ) => ["companies", "list", page, pageSize, options] as const,
  },
  individuals: {
    all: ["individuals"] as const,
    detail: (id: string) => ["individuals", "detail", id] as const,
    list: (
      page: number,
      pageSize: number,
      options: { search?: string; packageId?: string | number }
    ) => ["individuals", "list", page, pageSize, options] as const,
  },
  users: {
    all: ["users"] as const,
    detail: (id: string) => ["users", "detail", id] as const,
    registerData: (userId: string) =>
      ["users", "registerData", userId] as const,
    list: (
      page: number,
      pageSize: number,
      options: { search?: string; emailSearch?: string }
    ) => ["users", "list", page, pageSize, options] as const,
  },
  lookups: {
    all: ["lookups"] as const,
    parents: ["lookups", "parents"] as const,
    detail: (id: string) => ["lookups", "detail", id] as const,
    byParent: (
      parentCode: string,
      languageId: number,
      includeId?: string | number | null
    ) =>
      ["lookups", "byParent", parentCode, languageId, includeId ?? ""] as const,
    list: (
      page: number,
      pageSize: number,
      languageId: number,
      options: { search?: string; parentLookupId?: string; isDraft?: boolean }
    ) => ["lookups", "list", page, pageSize, languageId, options] as const,
  },
  industries: {
    options: (includeId?: string | number | null) =>
      ["industries", "options", includeId ?? ""] as const,
  },
  companyStatuses: {
    options: (selectedStatusId?: string | number | null, filterForEdit = false) =>
      [
        "companyStatuses",
        "options",
        selectedStatusId ?? "",
        filterForEdit,
      ] as const,
  },
  packages: {
    all: ["packages"] as const,
    backOffice: ["packages", "backOffice"] as const,
    detail: (id: string) => ["packages", "detail", id] as const,
    list: (
      page: number,
      pageSize: number,
      options?: {
        search?: string;
        isActivated?: boolean;
        isPublic?: boolean;
      }
    ) => ["packages", "list", page, pageSize, options ?? {}] as const,
  },
  licenses: {
    all: ["licenses"] as const,
    list: (
      page: number,
      pageSize: number,
      options: { search?: string; isActive?: boolean; isInUse?: boolean }
    ) => ["licenses", "list", page, pageSize, options] as const,
  },
  invoices: {
    all: ["invoices"] as const,
    list: (
      page: number,
      pageSize: number,
      options: {
        packageName?: string;
        type?: string;
        search?: string;
        fromDate?: string | null;
        toDate?: string | null;
      }
    ) => ["invoices", "list", page, pageSize, options] as const,
  },
  contactUs: {
    all: ["contactUs"] as const,
    detail: (id: string) => ["contactUs", "detail", id] as const,
    list: (page: number, pageSize: number) =>
      ["contactUs", "list", page, pageSize] as const,
  },
  countries: {
    all: ["countries"] as const,
  },
  publicProfile: {
    byKey: (kind: string, key: string) =>
      ["publicProfile", kind, key] as const,
  },
  license: {
    dashboard: (companyId?: number | string) =>
      ["license", "dashboard", companyId] as const,
  },
  dashboard: {
    admin: ["dashboard", "admin"] as const,
    demoClients: (
      page: number,
      pageSize: number,
      options: { search?: string; accountType?: string }
    ) => ["dashboard", "demoClients", page, pageSize, options] as const,
    nearExpiry: (
      page: number,
      pageSize: number,
      options: { search?: string; accountType?: string }
    ) => ["dashboard", "nearExpiry", page, pageSize, options] as const,
    expiredAccounts: (
      page: number,
      pageSize: number,
      options: { search?: string; accountType?: string; isDemo?: boolean }
    ) => ["dashboard", "expiredAccounts", page, pageSize, options] as const,
    scanAnalytics: (period: string, scope?: ScanAnalyticsScope) =>
      ["dashboard", "scanAnalytics", period, scopeKey(scope)] as const,
    recentScans: (
      scope: ScanAnalyticsScope | undefined,
      page: number,
      pageSize: number,
      filters: {
        from?: string | null;
        to?: string | null;
        employeeId?: string | null;
        individualId?: string | null;
      }
    ) =>
      [
        "dashboard",
        "recentScans",
        scopeKey(scope),
        page,
        pageSize,
        filters.from ?? "",
        filters.to ?? "",
        filters.employeeId ?? "",
        filters.individualId ?? "",
      ] as const,
  },
} as const;
